import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Plus, X, Loader2, Save } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SECTIONS = [
  { title: "Breakfast", items: "" },
  { title: "Mid-morning", items: "" },
  { title: "Lunch", items: "" },
  { title: "Evening", items: "" },
  { title: "Dinner", items: "" },
];

const input =
  "w-full bg-transparent border-b border-[#2C3C30]/25 focus:border-[#2C3C30] outline-none py-2.5 font-sans text-[#1A211D] placeholder:text-[#7D8C7A] transition-colors";
const box =
  "w-full bg-white border border-[#2C3C30]/15 rounded-xl px-4 py-3 font-sans text-[#1A211D] placeholder:text-[#7D8C7A] outline-none focus:border-[#2C3C30] transition-colors";

export default function AdminPrescriptionNew() {
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    patient_name: "",
    patient_phone: "",
    patient_email: "",
    consultation_date: new Date().toISOString().slice(0, 10),
    summary: "",
    notes: "",
  });
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [saving, setSaving] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updateSection = (i, k) => (e) =>
    setSections((list) => list.map((s, idx) => (idx === i ? { ...s, [k]: e.target.value } : s)));
  const addSection = () => setSections((l) => [...l, { title: "New meal", items: "" }]);
  const removeSection = (i) => setSections((l) => l.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_name.trim()) {
      toast.error("Patient name is required.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/prescriptions`,
        { ...form, diet_chart: sections.filter((s) => s.title.trim() && s.items.trim()) },
        { headers: authHeader() }
      );
      toast.success(`Saved as ${data.number}`);
      navigate(`/admin/prescriptions/${data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || JSON.stringify(d)).join(", ")
            : err.response?.status === 401
              ? "Your session expired. Please sign in again."
              : "Failed to save prescription. Please try again.";
      toast.error(msg);
      // eslint-disable-next-line no-console
      console.error("Prescription save failed", err.response?.status, err.response?.data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="admin-prescription-new">
      <header className="bg-[#2C3C30] text-[#F7F6F2]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-2 text-[#D1D6CC] text-sm hover:text-[#F7F6F2]">
              <ArrowLeft size={14} /> Dashboard
            </Link>
            <h1 className="font-serif text-3xl tracking-tight mt-1">New Prescription</h1>
          </div>
        </div>
      </header>

      <form onSubmit={submit} data-testid="prescription-form" className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 space-y-10">
        {/* Patient */}
        <div className="bg-white rounded-2xl border border-[#2C3C30]/10 p-6 md:p-8">
          <h2 className="font-serif text-2xl text-[#2C3C30] mb-6">Patient details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#566158]">Patient name *</label>
              <input className={input} value={form.patient_name} onChange={update("patient_name")} data-testid="pres-name" placeholder="e.g. Priya Sharma" />
            </div>
            <div>
              <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#566158]">Consultation date</label>
              <input type="date" className={input} value={form.consultation_date} onChange={update("consultation_date")} data-testid="pres-date" />
            </div>
            <div>
              <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#566158]">Phone</label>
              <input className={input} value={form.patient_phone} onChange={update("patient_phone")} data-testid="pres-phone" placeholder="+91 ..." />
            </div>
            <div>
              <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#566158]">Email</label>
              <input className={input} value={form.patient_email} onChange={update("patient_email")} data-testid="pres-email" placeholder="patient@email.com" />
            </div>
          </div>
          <div className="mt-6">
            <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#566158] block mb-1">Consultation summary</label>
            <textarea rows={3} className={box} value={form.summary} onChange={update("summary")} data-testid="pres-summary" placeholder="Chief complaint, assessment, goals..." />
          </div>
        </div>

        {/* Diet chart */}
        <div className="bg-white rounded-2xl border border-[#2C3C30]/10 p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-2xl text-[#2C3C30]">Diet chart</h2>
            <button
              type="button"
              onClick={addSection}
              data-testid="add-meal-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#EAE7E0] text-[#2C3C30] px-4 py-2 text-sm font-medium hover:bg-[#D1D6CC] transition-colors"
            >
              <Plus size={14} /> Add meal
            </button>
          </div>
          <div className="space-y-5">
            {sections.map((s, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start" data-testid={`meal-row-${i}`}>
                <div className="md:col-span-3">
                  <input
                    className={input}
                    value={s.title}
                    onChange={updateSection(i, "title")}
                    data-testid={`meal-title-${i}`}
                    placeholder="Meal name"
                  />
                </div>
                <div className="md:col-span-8">
                  <textarea
                    rows={2}
                    className={box}
                    value={s.items}
                    onChange={updateSection(i, "items")}
                    data-testid={`meal-items-${i}`}
                    placeholder="e.g. 1 bowl oats + banana + almonds; 1 cup green tea"
                  />
                </div>
                <div className="md:col-span-1 flex md:justify-end">
                  <button
                    type="button"
                    onClick={() => removeSection(i)}
                    className="text-[#a13a2f] hover:bg-[#a13a2f]/10 rounded-full p-2 transition-colors"
                    aria-label="Remove meal"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-2xl border border-[#2C3C30]/10 p-6 md:p-8">
          <h2 className="font-serif text-2xl text-[#2C3C30] mb-4">Notes & lifestyle recommendations</h2>
          <textarea rows={5} className={box} value={form.notes} onChange={update("notes")} data-testid="pres-notes" placeholder="Hydration, sleep, supplements, follow-up plan..." />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            data-testid="save-prescription-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#2C3C30] text-[#F7F6F2] px-8 py-3.5 font-medium hover:bg-[#1A211D] disabled:opacity-60 transition-colors"
          >
            {saving ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : <><Save size={16} /> Save prescription</>}
          </button>
        </div>
      </form>
    </div>
  );
}
